import { useEffect, useState } from "react";
import { api } from "./api";

// Module-level cache: { [page]: { content, expires } }
const CACHE_TTL_MS = 60 * 1000; // 1 minute as per spec
const cache = new Map();

/**
 * Reads the editable content for a page, with 60s in-memory cache.
 *
 * The API omits fields whose `visible` flag is false, so `content[key]`
 * returns:
 *   - a string (possibly empty "") when the field is visible
 *   - `undefined` when the field is hidden by the admin OR hasn't loaded yet
 *
 * Components decide what to render with `show(content, key)` below — this
 * differentiates "hidden/loading" (no DOM) from "visible but empty" (empty DOM).
 *
 * @returns { content, loading }
 */
export function usePageContent(page) {
  const [content, setContent] = useState(() => {
    const cached = cache.get(page);
    if (cached && cached.expires > Date.now()) return cached.content;
    return {};
  });
  const [loading, setLoading] = useState(() => {
    const cached = cache.get(page);
    return !(cached && cached.expires > Date.now());
  });

  useEffect(() => {
    let alive = true;
    const cached = cache.get(page);
    if (cached && cached.expires > Date.now()) {
      setContent(cached.content);
      setLoading(false);
      return () => {
        alive = false;
      };
    }
    api
      .get(`/page-content/${page}`)
      .then(({ data }) => {
        cache.set(page, { content: data, expires: Date.now() + CACHE_TTL_MS });
        if (alive) {
          setContent(data);
          setLoading(false);
        }
      })
      .catch(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [page]);

  return { content, loading };
}

/** Manually drop the cache entry for a page (called after admin save). */
export function invalidatePageContent(page) {
  cache.delete(page);
}

/**
 * Guard helper. Returns true if the key is present in the content object
 * (meaning: visible=true on the admin side — empty strings still count as
 * present). Use it to conditionally render DOM containers:
 *
 *     {show(content, "title") && <h1 className="whitespace-pre-line">{content.title}</h1>}
 */
export function show(content, key) {
  return content != null && Object.prototype.hasOwnProperty.call(content, key);
}
